import type { ObjectMetricExtension } from "@entrilia/shared/api/dataCollectionTypes";
import type {
  DataSubmissionBlockContent,
  LayoutBlock,
  MonthlyFinancialsBlockContent,
  QuarterlyDebtTableBlockContent,
  SingleExtendedMetricTableBlockContent,
} from "@entrilia/shared/api/inputFormTypes";
import type { StateAction } from "@entrilia/shared/types";
import { distinct, groupByToMap, stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { DataCollectionSubmissionDetails } from "../../../../api/types/dataCollectionTypes";

// Extract extended metrics from data submission contents

export interface ExtendedMetricInfo {
  id: string;
  metricIds: string[];
  name: string;
  extensionRows: MetricExtensionRow[];
}

export interface MetricExtensionRow {
  id: string;
  extensionIds: string[];
  extensionValue: string;
  hidden: boolean;
}

const getMetricsFromMonthlyFinancialsBlockContent = (
  blockId: string,
  blockContent: MonthlyFinancialsBlockContent
): ExtendedMetricInfo[] => {
  const metrics: ExtendedMetricInfo[] = [];

  for (const table of blockContent.tables) {
    for (const row of table.rows) {
      if (row.type === "ExtendedMetricSection") {
        metrics.push({
          id: `${blockId}_${row.metricId}`,
          metricIds: [row.metricId],
          name: row.name,
          extensionRows: [],
        });
      }
    }
  }

  return metrics;
};

const getMetricsFromQuarterlyDebtTableBlockContent = (
  blockId: string,
  blockContent: QuarterlyDebtTableBlockContent
): ExtendedMetricInfo[] => {
  const metricIds = blockContent.table.columns.map((col) => col.metricId);

  return metricIds.length === 0
    ? []
    : [
        {
          id: `${blockId}_${metricIds[0]}`,
          metricIds: distinct(metricIds),
          name: "All Metrics", // Aggregate all metrics into one entry to allow updates of extensions across all metrics
          extensionRows: [],
        },
      ];
};

const getMetricsFromSingleExtendedMetricTableBlockContent = (
  blockId: string,
  blockContent: SingleExtendedMetricTableBlockContent
): ExtendedMetricInfo[] => {
  if (blockContent.extensionConfiguration.staticExtensions.length === 0) {
    return [
      {
        id: `${blockId}_${blockContent.metricId}`,
        metricIds: [blockContent.metricId],
        name: blockContent.metricName,
        extensionRows: [],
      },
    ];
  }

  return [];
};

const getExtendedMetricFromBlockContent = (
  layoutBlock: LayoutBlock,
  blockContent: DataSubmissionBlockContent
): ExtendedMetricInfo[] => {
  switch (layoutBlock.type) {
    case "MonthlyFinancials":
      return getMetricsFromMonthlyFinancialsBlockContent(layoutBlock.id, blockContent as MonthlyFinancialsBlockContent);
    case "QuarterlyDebtTable":
      return getMetricsFromQuarterlyDebtTableBlockContent(
        layoutBlock.id,
        blockContent as QuarterlyDebtTableBlockContent
      );
    case "SingleExtendedMetricTable":
      return getMetricsFromSingleExtendedMetricTableBlockContent(
        layoutBlock.id,
        blockContent as SingleExtendedMetricTableBlockContent
      );
    case "InternalTable":
      return [];
    case "InfoSection":
      return [];
    default:
      return [];
  }
};

const getExtendedMetricsFromLayoutBlock = (
  layoutBlock: LayoutBlock,
  dataSubmission: DataCollectionSubmissionDetails
): ExtendedMetricInfo[] => {
  if (layoutBlock.type === "Container") {
    return layoutBlock.attributes.children.flatMap((childBlock) =>
      getExtendedMetricsFromLayoutBlock(childBlock, dataSubmission)
    );
  }

  const blockContent = dataSubmission.blockContents.find((content) => content.blockId === layoutBlock.id);
  return blockContent ? getExtendedMetricFromBlockContent(layoutBlock, blockContent.content) : [];
};

export const getExtendedMetricsFromSubmissionBlockContents = (
  dataSubmission: DataCollectionSubmissionDetails
): ExtendedMetricInfo[] =>
  dataSubmission.layout.blocks
    .flatMap((layoutBlock) => getExtendedMetricsFromLayoutBlock(layoutBlock, dataSubmission))
    .sort(stringComparerBy((m) => m.name));

// State

export interface MetricExtensionManagementState {
  metrics: ExtendedMetricInfo[];
  selectedMetric: ExtendedMetricInfo | undefined;
  searchTerm: string;
  updatedRowIds: Set<string>;
  renamedRowIds: Set<string>;
}

export const getInitialMetricExtensionManagementState = (
  dataSubmission: DataCollectionSubmissionDetails
): MetricExtensionManagementState => {
  const metrics = getExtendedMetricsFromSubmissionBlockContents(dataSubmission);

  return {
    metrics,
    selectedMetric: metrics[0],
    searchTerm: "",
    updatedRowIds: new Set(),
    renamedRowIds: new Set(),
  };
};

export const loadExtensionsAction =
  (extensions: ObjectMetricExtension[]): StateAction<MetricExtensionManagementState> =>
  (state) => {
    const newMetrics = [...state.metrics];

    for (const metric of newMetrics) {
      const relatedExtensions = extensions.filter((ext) => metric.metricIds.includes(ext.metricId));
      const extensionsByValue = groupByToMap(relatedExtensions, (ext) => ext.value);

      const metricExtensionRows: MetricExtensionRow[] = [...extensionsByValue.entries()].map(
        ([extValue, extensions]) => {
          const firstExtension = defined(extensions[0]);
          return {
            id: firstExtension.id,
            extensionIds: extensions.map((ext) => ext.id),
            extensionValue: extValue,
            hidden: Boolean(firstExtension.hidden),
          };
        }
      );

      metric.extensionRows = metricExtensionRows.sort(stringComparerBy((r) => r.extensionValue));
    }

    const selectedMetric = newMetrics[0];

    return {
      ...state,
      metrics: newMetrics,
      selectedMetric,
    };
  };

export const addExtensionsAction =
  (extensions: ObjectMetricExtension[]): StateAction<MetricExtensionManagementState> =>
  (state) => {
    const selectedMetric = state.selectedMetric;
    if (!selectedMetric) {
      return state;
    }

    const relatedExtensions = extensions.filter((ext) => selectedMetric.metricIds.includes(ext.metricId));
    if (relatedExtensions.length === 0) {
      return state;
    }

    const firstExtension = defined(relatedExtensions[0]);
    const extensionValue = firstExtension.value; // Extensions are created with the same value

    const newExtensionRow: MetricExtensionRow = {
      id: firstExtension.id,
      extensionIds: relatedExtensions.map((ext) => ext.id),
      extensionValue: extensionValue,
      hidden: Boolean(firstExtension.hidden),
    };

    if (selectedMetric.extensionRows.some((row) => row.id === newExtensionRow.id)) {
      return state;
    }

    selectedMetric.extensionRows = [...selectedMetric.extensionRows, newExtensionRow].sort(
      stringComparerBy((r) => r.extensionValue)
    );

    const newMetrics = state.metrics.map((metric) =>
      metric.id === selectedMetric.id ? { ...selectedMetric } : metric
    );

    return { ...state, metrics: newMetrics, selectedMetric };
  };

export const metricChangeAction =
  (id: string): StateAction<MetricExtensionManagementState> =>
  (state) => {
    const selectedMetric = state.metrics.find((metric) => metric.id === id);

    return {
      ...state,
      selectedMetric,
      searchTerm: "",
      updatedRowIds: new Set(),
      renamedRowIds: new Set(),
    };
  };

export const visibilityChangeAction =
  (rowId: string, visible: boolean): StateAction<MetricExtensionManagementState> =>
  (state) => {
    if (!state.selectedMetric) {
      return state;
    }

    const newExtensionRows = state.selectedMetric.extensionRows.map((row) =>
      row.id === rowId ? { ...row, hidden: !visible } : row
    );

    const newUpdatedRowIds = new Set(state.updatedRowIds);
    newUpdatedRowIds.add(rowId);

    return {
      ...state,
      selectedMetric: { ...state.selectedMetric, extensionRows: newExtensionRows },
      updatedRowIds: newUpdatedRowIds,
    };
  };

export const renameAction =
  (rowId: string, newExtensionValue: string): StateAction<MetricExtensionManagementState> =>
  (state) => {
    if (!state.selectedMetric) {
      return state;
    }

    const newExtensionRows = state.selectedMetric.extensionRows.map((row) =>
      row.id === rowId ? { ...row, extensionValue: newExtensionValue } : row
    );

    const newRenamedRowIds = new Set(state.renamedRowIds);
    newRenamedRowIds.add(rowId);

    return {
      ...state,
      selectedMetric: { ...state.selectedMetric, extensionRows: newExtensionRows },
      renamedRowIds: newRenamedRowIds,
    };
  };

export const updateExtensionRowsAction =
  (
    updatedExtensions: ObjectMetricExtension[],
    reset?: "updated" | "renamed"
  ): StateAction<MetricExtensionManagementState> =>
  (state) => {
    if (!state.selectedMetric) {
      return state;
    }

    const newExtensionRows = state.selectedMetric.extensionRows
      .map((row) => {
        const updatedExtension = updatedExtensions.find((ext) => row.extensionIds.includes(ext.id));
        return updatedExtension
          ? { ...row, extensionValue: updatedExtension.value, hidden: Boolean(updatedExtension.hidden) }
          : row;
      })
      .sort(stringComparerBy((r) => r.extensionValue));

    const selectedMetric = { ...state.selectedMetric, extensionRows: newExtensionRows };

    const newMetrics = state.metrics.map((metric) =>
      metric.id === selectedMetric.id ? { ...selectedMetric } : metric
    );

    return {
      ...state,
      selectedMetric,
      metrics: newMetrics,
      updatedRowIds: reset === "updated" ? new Set() : state.updatedRowIds,
      renamedRowIds: reset === "renamed" ? new Set() : state.renamedRowIds,
    };
  };

export const searchAction =
  (value: string): StateAction<MetricExtensionManagementState> =>
  (state) => ({
    ...state,
    searchTerm: value.trim().toLowerCase(),
  });
